import classNames from 'classnames';
import React, { ClipboardEventHandler, KeyboardEventHandler, useMemo } from 'react';
import { TextInput } from 'src/common/form/text-input';
import { checkInputForUrl } from 'src/common/helpers/check-input-for-url';
import { IconButton } from 'src/common/icon-button';
import { useDelayedUnmount } from 'src/common/spotlight/use-delayed-unmount.hook';
import { RightStrongIcon } from 'src/monet/icons';
import LoadingSpinner from 'src/common/components/loading-spinner';
import { useDashboardLibraryContentLinkStore } from 'src/dashboard/library/content/link-store';

interface Props {
  processInput?: (value?: string) => void;
  onUrlProcess?: (url: string) => void;
}

// TODO - standardize link inputs with a proper common name
export const DashboardLibraryContentAddPanelLinkInput = ({ onUrlProcess, processInput }: Props) => {
  const { inputValue, setInputValue, isLoading } = useDashboardLibraryContentLinkStore();

  const defaultInputProcessor = (value: string = inputValue) => {
    const url = checkInputForUrl(value);
    if (url && onUrlProcess) {
      onUrlProcess(url);
    }
  };

  const inputProcessor = useMemo(() => processInput || defaultInputProcessor, [processInput]);

  const isValidUrl = Boolean(checkInputForUrl(inputValue));
  const showEnterButton = Boolean(isValidUrl);
  const { mounted: mountedEnterButton } = useDelayedUnmount(showEnterButton);

  const onProcessButtonClicked = () => {
    inputProcessor(inputValue);
  };

  const onKeyDowned: KeyboardEventHandler = (event) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      inputProcessor(inputValue);
    }
  };

  const onInputPasted: ClipboardEventHandler = (event) => {
    let htmlText = event.clipboardData.getData('text/html');
    // texts pasted from "Notes" have html tags which cause /journey-nodes issue
    if (htmlText.includes('<body>')) {
      // it's a rich text, need to use only the content between body tags
      htmlText = htmlText.substring(htmlText.indexOf('<body>') + 1, htmlText.indexOf('</body>'));
    }
    inputProcessor(htmlText);
  };

  const onInputChanged: KeyboardEventHandler = (event) => {
    const { value } = event.target as HTMLInputElement;
    setInputValue(value);
  };

  return (
    <TextInput
      id='create-content-input'
      trailingAddon={
        <div
          className={classNames('transition-opacity opacity-0', {
            invisible: !isLoading && !mountedEnterButton,
            visible: mountedEnterButton || isLoading,
            'opacity-100': showEnterButton || isLoading,
          })}
        >
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <IconButton onClick={onProcessButtonClicked} onKeyDown={onKeyDowned}>
              <RightStrongIcon className='text-bedrock-gray-medium group-hover:text-bedrock-brand-secondary group-focus:text-bedrock-brand-secondary group-focus-visible:text-bedrock-brand-secondary group-focus-within:text-bedrock-brand-secondary' />
            </IconButton>
          )}
        </div>
      }
      inputClasses='pr-10'
      autoFocus
      onChange={onInputChanged}
      onKeyDown={onKeyDowned}
      inputProps={{
        type: 'text',
        name: 'create-content-input',
        onPaste: onInputPasted,
        value: inputValue,
        placeholder: 'Paste a URL or email',
      }}
    />
  );
};
